"use client";

import { c as _c } from "react/compiler-runtime";
import { format } from "date-fns";
import { ChevronLeft, ChevronUp, SquarePen, Trash2, MoreVerticalIcon, ExternalLink, LogOut, Image, FileText, Film, AudioLines, PenTool, Users, Search, Pin, PinOff } from "lucide-react";
// import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { List, type RowComponentProps } from "react-window";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { LogoIcon } from "@/components/ui/logo";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuAction, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { useChats, useDeleteChat, useUpdateChat, type Chat } from "@/hooks/useChats";
import { useOrganization } from "@/hooks/useOrganization";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { useAppConfig } from "@/lib/config";
import { CHAT_NAV_ITEM_IDS, filterEnabledNavItemIds, getSidebarLabels } from "@/lib/sidebar-nav";
import { ChatSearchDialog } from "./chat-search-dialog";
import { ChatSidebarSkeleton } from "./chat-sidebar-skeleton";
import { OrganizationSwitcher } from "./organization-switcher";
// import { ProjectSwitcher } from "./project-switcher";

import type { Organization, Project } from "@/lib/types";
export interface ChatSidebarHandle {
  scrollToTop: () => void;
}
interface ChatSidebarProps {
  currentChatId?: string;
  onChatSelect?: (chatId: string) => void;
  onNewChat?: () => void;
  className?: string;
  isLoading?: boolean;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  onOrganizationCreated: (organization: Organization) => void;
  projects: Project[];
  selectedProject: Project | null;
  onSelectProject: (project: Project | null) => void;
  onProjectCreated: (project: Project) => void;
}
type ChatHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "chat";
  key: string;
  chat: Chat;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 28;
const ROW_HEIGHT_SPACER = 6;
const ROW_HEIGHT_CHAT = 52;
const HISTORY_COLLAPSED_STORAGE_KEY = "playground-chat-history-collapsed";

// Ordered wf0-style sidebar entries for every non-chat nav id. Filtered at
// render time by ENABLED_NAV_ITEMS; labels resolve via `sidebar.nav.<id>`.
const SIDEBAR_NAV_ENTRIES = [{
  id: "image",
  href: "/image",
  icon: Image,
  fallback: "AI Image"
}, {
  id: "projects",
  href: "/projects",
  icon: FileText,
  fallback: "AI Documents"
}, {
  id: "video",
  href: "/video",
  icon: Film,
  fallback: "Video Studio"
}, {
  id: "audio",
  href: "/audio",
  icon: AudioLines,
  fallback: "Audio Studio"
}, {
  id: "canvas",
  href: "/canvas",
  icon: PenTool,
  fallback: "Canvas"
}, {
  id: "group",
  href: "/group",
  icon: Users,
  fallback: "Group Chat"
}] as const;
interface ChatHistoryRowProps {
  rows: ChatHistoryRow[];
  currentChatId?: string;
  editingId: string | null;
  editTitle: string;
  pendingFocusChatId: string | null;
  isPageLoading: boolean;
  isMobile: boolean;
  onChatSelect?: (chatId: string) => void;
  onEditTitleChange: (value: string) => void;
  onSaveTitle: (chatId: string) => void;
  onCancelEdit: () => void;
  onDeleteChat: (chatId: string) => void;
  onTogglePin: (chat: Chat) => void;
  onStartEdit: (chat: Chat) => void;
  onEditFocused: () => void;
}
function getChatHistoryRowHeight(index: number, {
  rows
}: ChatHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_CHAT;
}
function EditChatTitleInput(t0) {
  const $ = _c(16);
  const {
    chatId,
    value,
    shouldFocus,
    onChange,
    onSave,
    onCancel,
    onFocused
  } = t0;
  const inputRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== onFocused || $[1] !== shouldFocus) {
    t1 = () => {
      if (!shouldFocus || !inputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      onFocused();
    };
    t2 = [shouldFocus, onFocused];
    $[0] = onFocused;
    $[1] = shouldFocus;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== chatId || $[7] !== onSave) {
    t4 = () => onSave(chatId);
    $[6] = chatId;
    $[7] = onSave;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onCancel) {
    t5 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        e_0.currentTarget.blur();
      }
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        onCancel();
      }
    };
    $[9] = onCancel;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== value) {
    t6 = <Input ref={inputRef} value={value} onChange={t3} onBlur={t4} onKeyDown={t5} className="h-7 rounded-full border-none bg-transparent px-1 text-sm focus-visible:ring-0" />;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = value;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  return t6;
}
type ChatHistoryItemProps = Pick<ChatHistoryRowProps, "currentChatId" | "editingId" | "editTitle" | "pendingFocusChatId" | "isPageLoading" | "isMobile" | "onChatSelect" | "onEditTitleChange" | "onSaveTitle" | "onCancelEdit" | "onDeleteChat" | "onTogglePin" | "onStartEdit" | "onEditFocused"> & {
  chat: Chat;
};
function ChatHistoryItem(t0) {
  const $ = _c(26);
  const {
    chat,
    currentChatId,
    editingId,
    editTitle,
    pendingFocusChatId,
    isPageLoading,
    isMobile,
    onChatSelect,
    onEditTitleChange,
    onSaveTitle,
    onCancelEdit,
    onDeleteChat,
    onTogglePin,
    onStartEdit,
    onEditFocused
  } = t0;
  const isEditing = editingId === chat.id;
  const isActive = currentChatId === chat.id;
  const isActionFocusable = isActive && isMobile ? undefined : -1;
  let t1;
  if ($[0] !== chat.id || $[1] !== chat.messageCount || $[2] !== chat.title || $[3] !== chat.updatedAt || $[4] !== editTitle || $[5] !== isActive || $[6] !== isEditing || $[7] !== isPageLoading || $[8] !== onCancelEdit || $[9] !== onChatSelect || $[10] !== onEditFocused || $[11] !== onEditTitleChange || $[12] !== onSaveTitle || $[13] !== pendingFocusChatId) {
    t1 = isEditing ? <div className="flex h-full w-full items-center rounded-full bg-sidebar-accent px-2 pr-8 text-left text-sm text-sidebar-accent-foreground ring-sidebar-ring"><EditChatTitleInput chatId={chat.id} value={editTitle} shouldFocus={pendingFocusChatId === chat.id} onChange={onEditTitleChange} onSave={onSaveTitle} onCancel={onCancelEdit} onFocused={onEditFocused} /></div> : <SidebarMenuButton isActive={isActive} onClick={() => onChatSelect?.(chat.id)} className={["h-full! w-full justify-start group relative pr-2 !transition-none group-hover/chat-row:pr-[4.5rem]", isActive ? "max-md:pr-[4.5rem]" : ""].join(" ")} type="button" disabled={isPageLoading}><div className="flex-1 min-w-0"><div className="truncate text-sm font-medium mb-0.5">{chat.title}</div><div className="text-xs text-muted-foreground">{chat.messageCount} messages • {formatDate(chat.updatedAt)}</div></div></SidebarMenuButton>;
    $[0] = chat.id;
    $[1] = chat.messageCount;
    $[2] = chat.title;
    $[3] = chat.updatedAt;
    $[4] = editTitle;
    $[5] = isActive;
    $[6] = isEditing;
    $[7] = isPageLoading;
    $[8] = onCancelEdit;
    $[9] = onChatSelect;
    $[10] = onEditFocused;
    $[11] = onEditTitleChange;
    $[12] = onSaveTitle;
    $[13] = pendingFocusChatId;
    $[14] = t1;
  } else {
    t1 = $[14];
  }
  let t2;
  if ($[15] !== chat || $[16] !== isActionFocusable || $[17] !== isActive || $[18] !== isEditing || $[19] !== onDeleteChat || $[20] !== onStartEdit || $[21] !== onTogglePin) {
    t2 = !isEditing && <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"><button type="button" aria-label={chat.pinned ? "Unpin chat" : "Pin chat"} title={chat.pinned ? "Unpin chat" : "Pin chat"} tabIndex={isActionFocusable} onClick={e => {
        e.stopPropagation();
        onTogglePin(chat);
      }} className={["flex h-7 w-7 items-center justify-center rounded-full text-sidebar-foreground outline-hidden transition-opacity hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-sidebar-ring", "pointer-events-none opacity-0 group-hover/chat-row:pointer-events-auto group-hover/chat-row:opacity-100", isActive ? "max-md:pointer-events-auto max-md:opacity-100" : ""].join(" ")}>{chat.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}</button><DropdownMenu><DropdownMenuTrigger asChild={true}><SidebarMenuAction type="button" tabIndex={isActionFocusable} onClick={_temp} className={["pointer-events-none static hidden h-7 w-7 cursor-pointer opacity-0 group-hover/chat-row:flex group-hover/chat-row:pointer-events-auto group-hover/chat-row:opacity-100 data-[state=open]:flex data-[state=open]:pointer-events-auto data-[state=open]:opacity-100", isActive ? "max-md:flex max-md:pointer-events-auto max-md:opacity-100" : ""].join(" ")}><MoreVerticalIcon className="h-3.5 w-3.5" /></SidebarMenuAction></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-48"><DropdownMenuItem onClick={e_1 => {
            e_1.stopPropagation();
            onTogglePin(chat);
          }} className="flex items-center gap-2">{chat.pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}{chat.pinned ? "Unpin" : "Pin"}</DropdownMenuItem><DropdownMenuItem onClick={e_2 => {
            e_2.stopPropagation();
            onStartEdit(chat);
          }} className="flex items-center gap-2"><SquarePen className="h-4 w-4" />Rename</DropdownMenuItem><DropdownMenuItem onClick={e_3 => {
            e_3.stopPropagation();
            onDeleteChat(chat.id);
          }} className="flex items-center gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Delete</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>;
    $[15] = chat;
    $[16] = isActionFocusable;
    $[17] = isActive;
    $[18] = isEditing;
    $[19] = onDeleteChat;
    $[20] = onStartEdit;
    $[21] = onTogglePin;
    $[22] = t2;
  } else {
    t2 = $[22];
  }
  let t3;
  if ($[23] !== t1 || $[24] !== t2) {
    t3 = <div className="relative h-full px-2 pb-1"><div className="group/chat-row relative h-full">{t1}{t2}</div></div>;
    $[23] = t1;
    $[24] = t2;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  return t3;
}
function _temp(e_0) {
  e_0.stopPropagation();
}
function ChatHistoryRowComponent(t0) {
  const $ = _c(28);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentChatId,
    editingId,
    editTitle,
    pendingFocusChatId,
    isPageLoading,
    isMobile,
    onChatSelect,
    onEditTitleChange,
    onSaveTitle,
    onCancelEdit,
    onDeleteChat,
    onTogglePin,
    onStartEdit,
    onEditFocused
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  let t1;
  if ($[8] !== currentChatId || $[9] !== editTitle || $[10] !== editingId || $[11] !== isMobile || $[12] !== isPageLoading || $[13] !== onCancelEdit || $[14] !== onChatSelect || $[15] !== onDeleteChat || $[16] !== onEditFocused || $[17] !== onEditTitleChange || $[18] !== onSaveTitle || $[19] !== onStartEdit || $[20] !== onTogglePin || $[21] !== pendingFocusChatId || $[22] !== row.chat) {
    t1 = <ChatHistoryItem chat={row.chat} currentChatId={currentChatId} editingId={editingId} editTitle={editTitle} pendingFocusChatId={pendingFocusChatId} isPageLoading={isPageLoading} isMobile={isMobile} onChatSelect={onChatSelect} onEditTitleChange={onEditTitleChange} onSaveTitle={onSaveTitle} onCancelEdit={onCancelEdit} onDeleteChat={onDeleteChat} onTogglePin={onTogglePin} onStartEdit={onStartEdit} onEditFocused={onEditFocused} />;
    $[8] = currentChatId;
    $[9] = editTitle;
    $[10] = editingId;
    $[11] = isMobile;
    $[12] = isPageLoading;
    $[13] = onCancelEdit;
    $[14] = onChatSelect;
    $[15] = onDeleteChat;
    $[16] = onEditFocused;
    $[17] = onEditTitleChange;
    $[18] = onSaveTitle;
    $[19] = onStartEdit;
    $[20] = onTogglePin;
    $[21] = pendingFocusChatId;
    $[22] = row.chat;
    $[23] = t1;
  } else {
    t1 = $[23];
  }
  let t2;
  if ($[24] !== ariaAttributes || $[25] !== style || $[26] !== t1) {
    t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
    $[24] = ariaAttributes;
    $[25] = style;
    $[26] = t1;
    $[27] = t2;
  } else {
    t2 = $[27];
  }
  return t2;
}
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  if (diffInHours < 1) {
    return "Just now";
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else if (diffInHours < 48) {
    return "Yesterday";
  } else {
    return format(date, "MMM d");
  }
}
function groupChatsByDate(chats: Chat[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as Chat[],
    yesterday: [] as Chat[],
    lastWeek: [] as Chat[],
    older: [] as Chat[]
  };
  chats.forEach(chat => {
    const chatDate = new Date(chat.updatedAt);
    if (chatDate.toDateString() === today.toDateString()) {
      groups.today.push(chat);
    } else if (chatDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(chat);
    } else if (chatDate >= lastWeek) {
      groups.lastWeek.push(chat);
    } else {
      groups.older.push(chat);
    }
  });
  return groups;
}
export const ChatSidebar = function ChatSidebar(t0) {
  const $ = _c(236);
  const {
    ref,
    currentChatId,
    onChatSelect,
    onNewChat,
    className,
    isLoading: t1,
    organizations,
    selectedOrganization,
    onSelectOrganization
  } = t0;
  const isPageLoading = t1 === undefined ? false : t1;
  const listContainerRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => ({
      scrollToTop: () => {
        const scrollEl = listContainerRef.current?.firstElementChild as HTMLElement | null;
        scrollEl?.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      }
    });
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  useImperativeHandle(ref, t2);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t3;
  if ($[1] !== searchParams) {
    t3 = searchParams.get("orgId");
    $[1] = searchParams;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const orgIdParam = t3;
  let t4;
  if ($[3] !== orgIdParam) {
    t4 = path => orgIdParam ? `${path}?orgId=${orgIdParam}` : path;
    $[3] = orgIdParam;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const withOrg = t4;
  const {
    t
  } = useTranslation();
  const {
    enabledNavItems,
    loginUrl,
    dashboardUrl
  } = useAppConfig();
  const {
    state: sidebarState,
    isMobile,
    setOpenMobile,
    toggleSidebar
  } = useSidebar();
  const showOrganizationSwitcher = pathname === "/" || pathname === "/group";
  const router = useRouter();
  const posthog = usePostHog();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  let t5;
  if ($[5] !== loginUrl || $[6] !== posthog || $[7] !== router || $[8] !== signOut) {
    t5 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            router.push(loginUrl);
          }
        }
      });
    };
    $[5] = loginUrl;
    $[6] = posthog;
    $[7] = router;
    $[8] = signOut;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const logout = t5;
  const resolvedOrgId = selectedOrganization?.id ?? organization?.id;
  const {
    data: chatsData,
    isLoading: isChatsLoading
  } = useChats(resolvedOrgId);
  const deleteChat = useDeleteChat();
  const updateChat = useUpdateChat();
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [pendingFocusChatId, setPendingFocusChatId] = useState(null);
  useState(_temp2);
  chatsData?.chats;
  let t6;
  if ($[10] !== chatsData?.chats) {
    t6 = chatsData?.chats ?? [];
    $[10] = chatsData?.chats;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const chats = t6;
  let t7;
  if ($[12] !== t) {
    t7 = getSidebarLabels(t);
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const labels = t7;
  const isSearchEnabled = pathname === "/";
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setIsSearchOpen(true);
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const openSearch = t8;
  let t10;
  let t9;
  if ($[15] !== isSearchEnabled) {
    t9 = () => {
      if (!isSearchEnabled) {
        setIsSearchOpen(false);
      }
    };
    t10 = [isSearchEnabled];
    $[15] = isSearchEnabled;
    $[16] = t10;
    $[17] = t9;
  } else {
    t10 = $[16];
    t9 = $[17];
  }
  useEffect(t9, t10);
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = chat => {
      setEditingId(chat.id);
      setEditTitle(chat.title);
      setPendingFocusChatId(chat.id);
    };
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const handleEditTitle = t11;
  let t12;
  if ($[19] !== chats || $[20] !== editTitle || $[21] !== updateChat) {
    t12 = chatId => {
      const nextTitle = editTitle.trim();
      const currentTitle = chats.find(chat_0 => chat_0.id === chatId)?.title.trim();
      if (nextTitle && nextTitle !== currentTitle) {
        updateChat.mutate({
          params: {
            path: {
              id: chatId
            }
          },
          body: {
            title: nextTitle
          }
        });
      }
      setEditingId(null);
      setEditTitle("");
      setPendingFocusChatId(null);
    };
    $[19] = chats;
    $[20] = editTitle;
    $[21] = updateChat;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const saveTitle = t12;
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      setEditingId(null);
      setEditTitle("");
      setPendingFocusChatId(null);
    };
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  const cancelEditTitle = t13;
  let t14;
  if ($[24] !== currentChatId || $[25] !== deleteChat || $[26] !== onNewChat) {
    t14 = chatId_0 => {
      deleteChat.mutate({
        params: {
          path: {
            id: chatId_0
          }
        }
      });
      if (currentChatId === chatId_0) {
        onNewChat?.();
      }
    };
    $[24] = currentChatId;
    $[25] = deleteChat;
    $[26] = onNewChat;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const handleDeleteChat = t14;
  let t15;
  if ($[28] !== isMobile || $[29] !== onChatSelect || $[30] !== setOpenMobile) {
    t15 = chatId_1 => {
      onChatSelect?.(chatId_1);
      if (isMobile) {
        setOpenMobile(false);
      }
    };
    $[28] = isMobile;
    $[29] = onChatSelect;
    $[30] = setOpenMobile;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const handleChatSelect = t15;
  let t16;
  if ($[32] !== updateChat) {
    t16 = chat_1 => {
      updateChat.mutate({
        params: {
          path: {
            id: chat_1.id
          }
        },
        body: {
          pinned: !chat_1.pinned
        }
      });
    };
    $[32] = updateChat;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  const handleTogglePin = t16;
  let t17;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      setPendingFocusChatId(null);
    };
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  const onEditFocused = t17;
  let t18;
  if ($[35] !== chats) {
    t18 = [...chats].sort(_temp3);
    $[35] = chats;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const sortedChats = t18;
  let t19;
  if ($[37] !== sortedChats) {
    t19 = sortedChats.filter(_temp4);
    $[37] = sortedChats;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const pinnedChats = t19;
  let t20;
  if ($[39] !== sortedChats) {
    t20 = groupChatsByDate(sortedChats.filter(_temp5));
    $[39] = sortedChats;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const chatGroups = t20;
  let rows;
  if ($[41] !== chatGroups.lastWeek || $[42] !== chatGroups.older || $[43] !== chatGroups.today || $[44] !== chatGroups.yesterday || $[45] !== labels.timeGroups.last7Days || $[46] !== labels.timeGroups.older || $[47] !== labels.timeGroups.today || $[48] !== labels.timeGroups.yesterday) {
    const groups = [{
      title: labels.timeGroups.today,
      chats: chatGroups.today
    }, {
      title: labels.timeGroups.yesterday,
      chats: chatGroups.yesterday
    }, {
      title: labels.timeGroups.last7Days,
      chats: chatGroups.lastWeek
    }, {
      title: labels.timeGroups.older,
      chats: chatGroups.older
    }];
    rows = [];
    groups.forEach((t21, groupIndex) => {
      const {
        title,
        chats: groupedChats
      } = t21;
      if (groupedChats.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedChats.forEach(chat_4 => {
        rows.push({
          type: "chat",
          key: `chat-${chat_4.id}`,
          chat: chat_4
        });
      });
      const hasNextGroupWithChats = groups.slice(groupIndex + 1).some(_temp6);
      if (hasNextGroupWithChats) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[41] = chatGroups.lastWeek;
    $[42] = chatGroups.older;
    $[43] = chatGroups.today;
    $[44] = chatGroups.yesterday;
    $[45] = labels.timeGroups.last7Days;
    $[46] = labels.timeGroups.older;
    $[47] = labels.timeGroups.today;
    $[48] = labels.timeGroups.yesterday;
    $[49] = rows;
  } else {
    rows = $[49];
  }
  const historyRows = rows;
  let t21;
  if ($[50] !== currentChatId || $[51] !== editTitle || $[52] !== editingId || $[53] !== handleChatSelect || $[54] !== handleDeleteChat || $[55] !== handleTogglePin || $[56] !== historyRows || $[57] !== isMobile || $[58] !== isPageLoading || $[59] !== pendingFocusChatId || $[60] !== saveTitle) {
    t21 = {
      rows: historyRows,
      currentChatId,
      editingId,
      editTitle,
      pendingFocusChatId,
      isPageLoading,
      isMobile,
      onChatSelect: handleChatSelect,
      onEditTitleChange: setEditTitle,
      onSaveTitle: saveTitle,
      onCancelEdit: cancelEditTitle,
      onDeleteChat: handleDeleteChat,
      onTogglePin: handleTogglePin,
      onStartEdit: handleEditTitle,
      onEditFocused
    };
    $[50] = currentChatId;
    $[51] = editTitle;
    $[52] = editingId;
    $[53] = handleChatSelect;
    $[54] = handleDeleteChat;
    $[55] = handleTogglePin;
    $[56] = historyRows;
    $[57] = isMobile;
    $[58] = isPageLoading;
    $[59] = pendingFocusChatId;
    $[60] = saveTitle;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  const rowProps = t21;
  const isAuthenticated = !!user;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  let t22;
  if ($[62] !== enabledNavItems) {
    t22 = new Set(filterEnabledNavItemIds(CHAT_NAV_ITEM_IDS, enabledNavItems));
    $[62] = enabledNavItems;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  const enabled = t22;
  let t23;
  if ($[64] !== enabled || $[65] !== t) {
    let t24;
    if ($[67] !== enabled) {
      t24 = entry => enabled.has(entry.id);
      $[67] = enabled;
      $[68] = t24;
    } else {
      t24 = $[68];
    }
    let t25;
    if ($[69] !== t) {
      t25 = entry_0 => ({
        ...entry_0,
        label: t(`sidebar.nav.${entry_0.id}`, {
          defaultValue: entry_0.fallback
        })
      });
      $[69] = t;
      $[70] = t25;
    } else {
      t25 = $[70];
    }
    t23 = SIDEBAR_NAV_ENTRIES.filter(t24).map(t25);
    $[64] = enabled;
    $[65] = t;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  const navItems = t23;
  const isCollapsed = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t24;
    if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <ChatSidebarSkeleton organization={null} isOrgLoading={true} />;
      $[71] = t24;
    } else {
      t24 = $[71];
    }
    return t24;
  }
  if (!isAuthenticated) {
    let t24;
    if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
      t24 = <LogoIcon className="!size-6" />;
      $[72] = t24;
    } else {
      t24 = $[72];
    }
    let t25;
    if ($[73] !== labels.brand) {
      t25 = <h1 className="text-xl font-semibold">{labels.brand}</h1>;
      $[73] = labels.brand;
      $[74] = t25;
    } else {
      t25 = $[74];
    }
    let t26;
    if ($[75] !== t) {
      t26 = t("sidebar.nav.chat", {
        defaultValue: "Chat"
      });
      $[75] = t;
      $[76] = t26;
    } else {
      t26 = $[76];
    }
    let t27;
    if ($[77] !== t26) {
      t27 = <Badge>{t26}</Badge>;
      $[77] = t26;
      $[78] = t27;
    } else {
      t27 = $[78];
    }
    let t28;
    if ($[79] !== t25 || $[80] !== t27) {
      t28 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}>{t24}{t25}{t27}</Link>;
      $[79] = t25;
      $[80] = t27;
      $[81] = t28;
    } else {
      t28 = $[81];
    }
    let t29;
    if ($[82] !== labels.signInRequired) {
      t29 = <div className="font-medium mb-2">{labels.signInRequired}</div>;
      $[82] = labels.signInRequired;
      $[83] = t29;
    } else {
      t29 = $[83];
    }
    let t30;
    if ($[84] !== labels.signInForChatSidebar) {
      t30 = <p className="text-muted-foreground mb-3">{labels.signInForChatSidebar}</p>;
      $[84] = labels.signInForChatSidebar;
      $[85] = t30;
    } else {
      t30 = $[85];
    }
    let t31;
    if ($[86] !== labels.signIn) {
      t31 = <Button size="sm" asChild={true}><Link href="/login">{labels.signIn}</Link></Button>;
      $[86] = labels.signIn;
      $[87] = t31;
    } else {
      t31 = $[87];
    }
    let t32;
    if ($[88] !== labels.createAccount) {
      t32 = <Button size="sm" variant="outline" asChild={true}><Link href="/signup">{labels.createAccount}</Link></Button>;
      $[88] = labels.createAccount;
      $[89] = t32;
    } else {
      t32 = $[89];
    }
    let t33;
    if ($[90] !== t31 || $[91] !== t32) {
      t33 = <div className="flex items-center justify-end gap-2">{t31}{t32}</div>;
      $[90] = t31;
      $[91] = t32;
      $[92] = t33;
    } else {
      t33 = $[92];
    }
    let t34;
    if ($[93] !== t29 || $[94] !== t30 || $[95] !== t33) {
      t34 = <div className="w-full rounded-md border p-4 text-sm">{t29}{t30}{t33}</div>;
      $[93] = t29;
      $[94] = t30;
      $[95] = t33;
      $[96] = t34;
    } else {
      t34 = $[96];
    }
    let t35;
    if ($[97] !== t28 || $[98] !== t34) {
      t35 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t28}{t34}</div></SidebarHeader>;
      $[97] = t28;
      $[98] = t34;
      $[99] = t35;
    } else {
      t35 = $[99];
    }
    let t36;
    if ($[100] !== className || $[101] !== t35) {
      t36 = <Sidebar className={className}>{t35}</Sidebar>;
      $[100] = className;
      $[101] = t35;
      $[102] = t36;
    } else {
      t36 = $[102];
    }
    return t36;
  }
  if (isChatsLoading || isOrgLoading) {
    let t24;
    if ($[103] !== isOrgLoading || $[104] !== selectedOrganization) {
      t24 = <ChatSidebarSkeleton organization={selectedOrganization} isOrgLoading={isOrgLoading} />;
      $[103] = isOrgLoading;
      $[104] = selectedOrganization;
      $[105] = t24;
    } else {
      t24 = $[105];
    }
    return t24;
  }
  const t24 = className + " max-md:hidden border-r border-[#e5e5e5] bg-[#fafafa]";
  let t25;
  if ($[106] !== isCollapsed || $[107] !== toggleSidebar) {
    t25 = () => {
      if (isCollapsed) {
        toggleSidebar();
      }
    };
    $[106] = isCollapsed;
    $[107] = toggleSidebar;
    $[108] = t25;
  } else {
    t25 = $[108];
  }
  let t26;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <LogoIcon className="!size-6" />;
    $[109] = t26;
  } else {
    t26 = $[109];
  }
  let t27;
  if ($[110] !== isCollapsed || $[111] !== labels.brand || $[112] !== t) {
    t27 = isCollapsed ? null : <><span className="text-[1.05rem] font-bold leading-none tracking-tight">{labels.brand}</span><Badge variant="secondary" className="group-data-[collapsible=icon]:hidden">{t("sidebar.nav.chat", {
          defaultValue: "Chat"
        })}</Badge></>;
    $[110] = isCollapsed;
    $[111] = labels.brand;
    $[112] = t;
    $[113] = t27;
  } else {
    t27 = $[113];
  }
  let t28;
  if ($[114] !== t25 || $[115] !== t27) {
    t28 = <button type="button" onClick={t25} className="flex items-center gap-3">{t26}{t27}</button>;
    $[114] = t25;
    $[115] = t27;
    $[116] = t28;
  } else {
    t28 = $[116];
  }
  let t29;
  if ($[117] !== isCollapsed || $[118] !== toggleSidebar) {
    t29 = isCollapsed ? null : <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full" onClick={event => {
      event.stopPropagation();
      toggleSidebar();
    }}><ChevronLeft className="h-4 w-4 text-muted-foreground" /></Button>;
    $[117] = isCollapsed;
    $[118] = toggleSidebar;
    $[119] = t29;
  } else {
    t29 = $[119];
  }
  let t30;
  if ($[120] !== t28 || $[121] !== t29) {
    t30 = <div className="flex h-16 items-center justify-between px-3">{t28}{t29}</div>;
    $[120] = t28;
    $[121] = t29;
    $[122] = t30;
  } else {
    t30 = $[122];
  }
  const t31 = !isSearchEnabled;
  const t32 = isCollapsed ? "mx-auto h-11 w-11 justify-center rounded-full bg-transparent text-[#666] hover:bg-[#ececed]" : "h-11 rounded-full bg-transparent px-4 text-[0.92rem] font-normal text-[#5a5a5a] hover:bg-[#eeeeef]";
  let t33;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Search className="h-5 w-5" />;
    $[123] = t33;
  } else {
    t33 = $[123];
  }
  let t34;
  if ($[124] !== isCollapsed || $[125] !== labels.searchChats) {
    t34 = isCollapsed ? null : <span>{labels.searchChats}</span>;
    $[124] = isCollapsed;
    $[125] = labels.searchChats;
    $[126] = t34;
  } else {
    t34 = $[126];
  }
  let t35;
  if ($[127] !== t31 || $[128] !== t32 || $[129] !== t34) {
    t35 = <SidebarMenuItem><SidebarMenuButton type="button" disabled={t31} onClick={openSearch} className={t32}>{t33}{t34}</SidebarMenuButton></SidebarMenuItem>;
    $[127] = t31;
    $[128] = t32;
    $[129] = t34;
    $[130] = t35;
  } else {
    t35 = $[130];
  }
  let t36;
  if ($[131] !== isCollapsed || $[132] !== isPageLoading || $[133] !== labels.newChat || $[134] !== onNewChat) {
    t36 = isCollapsed ? <SidebarMenuItem><SidebarMenuButton onClick={onNewChat} className="mx-auto h-11 w-11 justify-center rounded-full bg-transparent text-[#5a5a5a] hover:bg-[#ececed]"><SquarePen className="h-5 w-5" /></SidebarMenuButton></SidebarMenuItem> : <SidebarMenuItem><SidebarMenuButton onClick={onNewChat} disabled={isPageLoading} className="h-11 rounded-full border border-[#00000010] bg-white px-[11px] text-[0.92rem] font-semibold text-[#5a5a5a] hover:bg-[#f0f0f0]"><SquarePen className="h-5 w-5" /><span>{labels.newChat}</span></SidebarMenuButton></SidebarMenuItem>;
    $[131] = isCollapsed;
    $[132] = isPageLoading;
    $[133] = labels.newChat;
    $[134] = onNewChat;
    $[135] = t36;
  } else {
    t36 = $[135];
  }
  let t37;
  if ($[136] !== isCollapsed || $[137] !== navItems || $[138] !== pathname || $[139] !== withOrg) {
    let t38;
    if ($[141] !== isCollapsed || $[142] !== pathname || $[143] !== withOrg) {
      t38 = item => {
        const Icon = item.icon;
        return <SidebarMenuItem key={item.id}><SidebarMenuButton asChild={true} isActive={pathname === item.href} className={isCollapsed ? "mx-auto mt-2 h-11 w-11 justify-center rounded-full bg-transparent text-[#666] hover:bg-[#ececed]" : "h-11 rounded-full bg-transparent px-4 text-[0.92rem] font-normal text-[#5a5a5a] hover:bg-[#eeeeef]"}><Link href={withOrg(item.href)} prefetch={true}><Icon className="h-5 w-5" />{isCollapsed ? null : <span>{item.label}</span>}</Link></SidebarMenuButton></SidebarMenuItem>;
      };
      $[141] = isCollapsed;
      $[142] = pathname;
      $[143] = withOrg;
      $[144] = t38;
    } else {
      t38 = $[144];
    }
    t37 = navItems.map(t38);
    $[136] = isCollapsed;
    $[137] = navItems;
    $[138] = pathname;
    $[139] = withOrg;
    $[140] = t37;
  } else {
    t37 = $[140];
  }
  let t38;
  if ($[145] !== t35 || $[146] !== t36 || $[147] !== t37) {
    t38 = <SidebarMenu className="space-y-1 px-1 pb-1">{t35}{t36}{t37}</SidebarMenu>;
    $[145] = t35;
    $[146] = t36;
    $[147] = t37;
    $[148] = t38;
  } else {
    t38 = $[148];
  }
  let t39;
  if ($[149] !== t30 || $[150] !== t38) {
    t39 = <SidebarHeader className="px-[10px] py-0">{t30}{t38}</SidebarHeader>;
    $[149] = t30;
    $[150] = t38;
    $[151] = t39;
  } else {
    t39 = $[151];
  }
  let t40;
  if ($[152] !== handleChatSelect || $[153] !== isSearchEnabled || $[154] !== isSearchOpen || $[155] !== onNewChat) {
    t40 = isSearchEnabled ? <ChatSearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} onNewChat={onNewChat} onChatSelect={handleChatSelect} /> : null;
    $[152] = handleChatSelect;
    $[153] = isSearchEnabled;
    $[154] = isSearchOpen;
    $[155] = onNewChat;
    $[156] = t40;
  } else {
    t40 = $[156];
  }
  const t41 = isCollapsed ? "overflow-hidden px-[10px] pb-2" : "overflow-hidden px-[10px] pb-2";
  let t42;
  if ($[157] !== isCollapsed || $[158] !== onSelectOrganization || $[159] !== organizations || $[160] !== selectedOrganization || $[161] !== showOrganizationSwitcher) {
    t42 = organizations.length > 0 && showOrganizationSwitcher && !isCollapsed ? <div className="shrink-0"><div className="mx-3 mb-1 border-t border-[#e5e5e5]" /><SidebarMenu className="space-y-1 px-1 pb-1"><SidebarMenuItem><OrganizationSwitcher organizations={organizations} selectedOrganization={selectedOrganization} onSelectOrganization={onSelectOrganization} triggerClassName="h-11 rounded-full bg-transparent px-4 text-[0.92rem] font-normal text-[#5a5a5a] hover:bg-[#eeeeef]" /></SidebarMenuItem></SidebarMenu></div> : null;
    $[157] = isCollapsed;
    $[158] = onSelectOrganization;
    $[159] = organizations;
    $[160] = selectedOrganization;
    $[161] = showOrganizationSwitcher;
    $[162] = t42;
  } else {
    t42 = $[162];
  }
  let t43;
  if ($[163] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <div className="px-3 py-2 text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-[#8a8a8a]">Chat History</div>;
    $[163] = t43;
  } else {
    t43 = $[163];
  }
  let t44;
  if ($[164] !== chats.length || $[165] !== currentChatId || $[166] !== editTitle || $[167] !== editingId || $[168] !== handleChatSelect || $[169] !== handleDeleteChat || $[170] !== handleTogglePin || $[171] !== historyRows || $[172] !== isMobile || $[173] !== isPageLoading || $[174] !== labels.pinned || $[175] !== pendingFocusChatId || $[176] !== pinnedChats || $[177] !== rowProps || $[178] !== saveTitle) {
    t44 = chats.length === 0 ? <div className="px-[14px] text-left"><p className="text-[0.8rem] text-[#7a7a7a]">No chat history.</p><p className="mt-1 text-[0.75rem] text-[#b1b1b1]">Create one to get started.</p></div> : <>{pinnedChats.length > 0 && <div className="shrink-0 max-h-[45%] overflow-y-auto border-b border-sidebar-border/60 pb-1"><div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{labels.pinned}</div>{pinnedChats.map(chat_5 => <div key={chat_5.id} style={{
          height: ROW_HEIGHT_CHAT
        }}><ChatHistoryItem chat={chat_5} currentChatId={currentChatId} editingId={editingId} editTitle={editTitle} pendingFocusChatId={pendingFocusChatId} isPageLoading={isPageLoading} isMobile={isMobile} onChatSelect={handleChatSelect} onEditTitleChange={setEditTitle} onSaveTitle={saveTitle} onCancelEdit={cancelEditTitle} onDeleteChat={handleDeleteChat} onTogglePin={handleTogglePin} onStartEdit={handleEditTitle} onEditFocused={onEditFocused} /></div>)}</div>}{historyRows.length > 0 && <List className="min-h-0 w-full flex-1" style={{
        width: "100%"
      }} rowComponent={ChatHistoryRowComponent} rowCount={historyRows.length} rowHeight={getChatHistoryRowHeight} rowProps={rowProps} overscanCount={8} />}</>;
    $[164] = chats.length;
    $[165] = currentChatId;
    $[166] = editTitle;
    $[167] = editingId;
    $[168] = handleChatSelect;
    $[169] = handleDeleteChat;
    $[170] = handleTogglePin;
    $[171] = historyRows;
    $[172] = isMobile;
    $[173] = isPageLoading;
    $[174] = labels.pinned;
    $[175] = pendingFocusChatId;
    $[176] = pinnedChats;
    $[177] = rowProps;
    $[178] = saveTitle;
    $[179] = t44;
  } else {
    t44 = $[179];
  }
  let t45;
  if ($[180] !== isHistoryHidden || $[181] !== t44) {
    t45 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t43}{t44}</div>;
    $[180] = isHistoryHidden;
    $[181] = t44;
    $[182] = t45;
  } else {
    t45 = $[182];
  }
  let t46;
  if ($[183] !== t41 || $[184] !== t42 || $[185] !== t45) {
    t46 = <SidebarContent className={t41}>{t42}{t45}</SidebarContent>;
    $[183] = t41;
    $[184] = t42;
    $[185] = t45;
    $[186] = t46;
  } else {
    t46 = $[186];
  }
  let t47;
  if ($[187] !== isCollapsed || $[188] !== isOrgLoading || $[189] !== organization || $[190] !== selectedOrganization) {
    t47 = isCollapsed ? null : <div className="mb-1"><CreditsDisplay organization={!selectedOrganization || selectedOrganization.kind === "chat" ? organization : selectedOrganization} isLoading={isOrgLoading} isChatPlanOrg={!selectedOrganization || selectedOrganization.kind === "chat"} /></div>;
    $[187] = isCollapsed;
    $[188] = isOrgLoading;
    $[189] = organization;
    $[190] = selectedOrganization;
    $[191] = t47;
  } else {
    t47 = $[191];
  }
  const t48 = user?.name ?? labels.user;
  const t49 = isCollapsed ? "mx-auto h-11 w-11 justify-center rounded-full hover:bg-[#ececed]" : "data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground";
  let t50;
  if ($[192] !== user?.name) {
    t50 = user?.name?.split(" ").map(_temp7).join("").toUpperCase().slice(0, 2) ?? "U";
    $[192] = user?.name;
    $[193] = t50;
  } else {
    t50 = $[193];
  }
  let t51;
  if ($[194] !== t50) {
    t51 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t50}</span></div>;
    $[194] = t50;
    $[195] = t51;
  } else {
    t51 = $[195];
  }
  let t52;
  if ($[196] !== isCollapsed || $[197] !== user?.email || $[198] !== user?.name) {
    t52 = isCollapsed ? null : <><div className="grid flex-1 text-left text-sm leading-tight"><span className="truncate font-semibold">{user?.name}</span><span className="truncate text-xs text-muted-foreground">{user?.email}</span></div><ChevronUp className="ml-auto size-4" /></>;
    $[196] = isCollapsed;
    $[197] = user?.email;
    $[198] = user?.name;
    $[199] = t52;
  } else {
    t52 = $[199];
  }
  let t53;
  if ($[200] !== t48 || $[201] !== t49 || $[202] !== t51 || $[203] !== t52) {
    t53 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t48} className={t49}>{t51}{t52}</SidebarMenuButton></DropdownMenuTrigger>;
    $[200] = t48;
    $[201] = t49;
    $[202] = t51;
    $[203] = t52;
    $[204] = t53;
  } else {
    t53 = $[204];
  }
  let t54;
  if ($[205] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <ExternalLink className="mr-2 h-4 w-4" />;
    $[205] = t54;
  } else {
    t54 = $[205];
  }
  let t55;
  if ($[206] !== dashboardUrl || $[207] !== labels.dashboard) {
    t55 = <DropdownMenuItem asChild={true}><a href={dashboardUrl} target="_blank" rel="noopener noreferrer">{t54}{labels.dashboard}</a></DropdownMenuItem>;
    $[206] = dashboardUrl;
    $[207] = labels.dashboard;
    $[208] = t55;
  } else {
    t55 = $[208];
  }
  let t56;
  if ($[209] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <DropdownMenuSeparator />;
    $[209] = t56;
  } else {
    t56 = $[209];
  }
  let t57;
  if ($[210] !== labels.theme) {
    t57 = <span>{labels.theme}</span>;
    $[210] = labels.theme;
    $[211] = t57;
  } else {
    t57 = $[211];
  }
  let t58;
  if ($[212] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <div className="flex items-center gap-2"><div onClick={_temp9} onKeyDown={_temp10}><ThemeToggle className="shrink-0" size="compact" /></div></div>;
    $[212] = t58;
  } else {
    t58 = $[212];
  }
  let t59;
  if ($[213] !== t57) {
    t59 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp8}>{t57}{t58}</DropdownMenuItem>;
    $[213] = t57;
    $[214] = t59;
  } else {
    t59 = $[214];
  }
  let t60;
  if ($[215] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <DropdownMenuSeparator />;
    $[215] = t60;
  } else {
    t60 = $[215];
  }
  let t61;
  if ($[216] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = <LogOut className="mr-2 h-4 w-4" />;
    $[216] = t61;
  } else {
    t61 = $[216];
  }
  let t62;
  if ($[217] !== labels.logOut || $[218] !== logout) {
    t62 = <DropdownMenuItem onClick={logout}>{t61}{labels.logOut}</DropdownMenuItem>;
    $[217] = labels.logOut;
    $[218] = logout;
    $[219] = t62;
  } else {
    t62 = $[219];
  }
  let t63;
  if ($[220] !== t55 || $[221] !== t59 || $[222] !== t62) {
    t63 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t55}{t56}{t59}{t60}{t62}</DropdownMenuContent>;
    $[220] = t55;
    $[221] = t59;
    $[222] = t62;
    $[223] = t63;
  } else {
    t63 = $[223];
  }
  let t64;
  if ($[224] !== t53 || $[225] !== t63) {
    t64 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t53}{t63}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[224] = t53;
    $[225] = t63;
    $[226] = t64;
  } else {
    t64 = $[226];
  }
  let t65;
  if ($[227] !== t47 || $[228] !== t64) {
    t65 = <SidebarFooter className="mt-auto px-[10px] pb-3 pt-2">{t47}{t64}</SidebarFooter>;
    $[227] = t47;
    $[228] = t64;
    $[229] = t65;
  } else {
    t65 = $[229];
  }
  let t66;
  if ($[230] !== t24 || $[231] !== t39 || $[232] !== t40 || $[233] !== t46 || $[234] !== t65) {
    t66 = <Sidebar collapsible="icon" className={t24}>{t39}{t40}{t46}{t65}</Sidebar>;
    $[230] = t24;
    $[231] = t39;
    $[232] = t40;
    $[233] = t46;
    $[234] = t65;
    $[235] = t66;
  } else {
    t66 = $[235];
  }
  return t66;
};
function _temp2() {
  return typeof document !== "undefined" && document.cookie.split("; ").includes(`${HISTORY_COLLAPSED_STORAGE_KEY}=1`);
}
function _temp3(a, b) {
  return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
}
function _temp4(chat_2) {
  return chat_2.pinned;
}
function _temp5(chat_3) {
  return !chat_3.pinned;
}
function _temp6(group) {
  return group.chats.length > 0;
}
function _temp7(n) {
  return n[0];
}
function _temp8(event_0) {
  return event_0.preventDefault();
}
function _temp9(event_1) {
  return event_1.stopPropagation();
}
function _temp10(event_2) {
  return event_2.stopPropagation();
}